import { useState } from 'react';
import { Button, ButtonGroup, Badge, Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { OneShotSecretReveal } from '../../components/OneShotSecretReveal';
import { HasPermission } from '../../rbac/HasPermission';
import {
  useWebhooks,
  useDeleteWebhook,
  useRotateWebhookSecret,
  useUpdateWebhook,
} from './useWebhooks';
import { CreateWebhookModal } from './CreateWebhookModal';
import { DeliveriesCard } from './DeliveriesCard';

/**
 * Outbound-Webhooks Verwaltung.
 *
 * Spec: lmm-wiki/wiki/orbit-specs/outbound-webhooks-admin.md
 * Route: /:brandSlug/outbound-webhooks
 *
 * Layout: zwei Cards untereinander (Webhook-Liste + Deliveries-Tabelle).
 */
export function OutboundWebhooksPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingRotate, setPendingRotate] = useState(null);
  const [rotated, setRotated] = useState(null);

  const query = useWebhooks();
  const del = useDeleteWebhook();
  const rotate = useRotateWebhookSecret();
  const update = useUpdateWebhook();

  const webhooks = query.data?.webhooks ?? [];

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    await del.mutateAsync({ id: pendingDelete.id });
    setPendingDelete(null);
  };

  const onConfirmRotate = async () => {
    if (!pendingRotate) return;
    const res = await rotate.mutateAsync({ id: pendingRotate.id });
    setPendingRotate(null);
    setRotated(res);
  };

  const onToggleActive = (hook) => {
    update.mutate({ id: hook.id, patch: { isActive: !hook.isActive } });
  };

  return (
    <div>
      <PageHeader
        title="Outbound-Webhooks"
        subtitle={`${webhooks.filter((w) => w.isActive).length} aktiv von ${webhooks.length} · brand-gebunden`}
        actions={
          <HasPermission perms={['outboundWebhooks.write']}>
            <Button color="primary" onClick={() => setCreateOpen(true)}>
              + Neuer Webhook
            </Button>
          </HasPermission>
        }
      />

      {webhooks.length === 0 && !query.isLoading && (
        <Alert color="info">
          Noch keine Webhooks angelegt. Webhooks empfangen HMAC-signierte JSON-Events
          bei Lifecycle-Aktionen (z. B. <code>user.anonymized</code>,
          <code>subscription.cancel_intent_confirmed</code>).
        </Alert>
      )}

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!webhooks.length}
        emptyMessage="Keine Webhooks."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 60 }}>ID</th>
            <th>Label</th>
            <th>URL</th>
            <th>Events</th>
            <th style={{ width: 110 }}>Aktiv</th>
            <th style={{ width: 150 }}>Letzter ✓</th>
            <th style={{ width: 150 }}>Letzter ✗</th>
            <th style={{ width: 200 }} />
          </tr>
        </thead>
        <tbody>
          {webhooks.map((h) => (
            <tr key={h.id} className={!h.isActive ? 'opacity-50' : undefined}>
              <td>#{h.id}</td>
              <td className="fw-semibold">{h.label}</td>
              <td>
                <CopyChip value={h.url} label="URL" />
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {h.eventFilters.map((e) => (
                    <Badge
                      key={e}
                      color="secondary"
                      pill
                      className="orbit-code-badge"
                    >
                      {e}
                    </Badge>
                  ))}
                </div>
              </td>
              <td>
                <StatusBadge value={h.isActive ? 'active' : 'suspended'} />
              </td>
              <td>
                <RelativeTime iso={h.lastSuccessAt} />
              </td>
              <td>
                {h.lastFailureAt ? (
                  <div>
                    <RelativeTime iso={h.lastFailureAt} />
                    {h.lastError && (
                      <div
                        className="small text-danger"
                        style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={h.lastError}
                      >
                        {h.lastError}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="text-end">
                <HasPermission perms={['outboundWebhooks.write']}>
                  <ButtonGroup size="sm">
                    <Button
                      color={h.isActive ? 'warning' : 'success'}
                      outline
                      onClick={() => onToggleActive(h)}
                      disabled={update.isPending}
                      title={h.isActive ? 'Webhook pausieren — keine neuen Deliveries' : 'Webhook reaktivieren'}
                    >
                      {h.isActive ? 'Pausieren' : 'Aktivieren'}
                    </Button>
                    <Button color="info" outline onClick={() => setPendingRotate(h)}>
                      🔑 Rotate
                    </Button>
                    <Button color="danger" outline onClick={() => setPendingDelete(h)}>
                      🗑 Löschen
                    </Button>
                  </ButtonGroup>
                </HasPermission>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <div className="mt-4">
        <DeliveriesCard webhooks={webhooks} />
      </div>

      <CreateWebhookModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        knownEventTypes={query.data?.knownEventTypes ?? []}
      />

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Webhook löschen?"
        body={
          pendingDelete ? (
            <>
              <p className="mb-2">
                <strong>{pendingDelete.label}</strong> wird endgültig entfernt. Pending
                und in-flight Deliveries werden abgebrochen.
              </p>
              <p className="small text-muted mb-0">
                Wenn der Empfänger nur kurz down ist: lieber <code>Pausieren</code>
                verwenden — Pause behält die Konfiguration.
              </p>
            </>
          ) : null
        }
        confirmLabel="Löschen"
        confirmColor="danger"
        busy={del.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        isOpen={!!pendingRotate}
        title="Webhook-Secret rotieren?"
        body={
          pendingRotate ? (
            <>
              <p className="mb-2">
                Neuer HMAC-Signing-Secret für <strong>{pendingRotate.label}</strong>.
                Bisheriger Secret wird <strong>sofort ungültig</strong> — Empfänger
                muss neu konfiguriert werden.
              </p>
            </>
          ) : null
        }
        confirmLabel="Jetzt rotieren"
        confirmColor="warning"
        busy={rotate.isPending}
        onConfirm={onConfirmRotate}
        onCancel={() => setPendingRotate(null)}
      />

      {rotated && (
        <OneShotSecretReveal
          isOpen
          label="Neuer HMAC-Signing-Secret"
          secret={rotated.secret}
          confirmPhrase="Empfänger wurde mit dem neuen Secret konfiguriert"
          onAcknowledged={() => setRotated(null)}
        />
      )}
    </div>
  );
}
